/**
 * UtilizationModal — Aug-2026 (User-Wunsch): Mechaniker-Auslastung der Woche
 * je Standort, damit Termine besser verteilt werden können.
 */
import { useEffect, useState } from "react";
import api from "../../lib/api";
import { X, ChevronLeft, ChevronRight, Loader2, Gauge } from "lucide-react";
import { Button } from "../ui/button";

const fmtH = (min) => {
    const m = Number(min) || 0;
    if (m === 0) return "0h";
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r ? `${h}:${String(r).padStart(2, "0")}h` : `${h}h`;
};

const pctStyle = (pct) => {
    if (pct == null) return "bg-gray-100 text-gray-400";
    if (pct >= 90) return "bg-red-100 text-red-800 border-red-300";
    if (pct >= 60) return "bg-amber-100 text-amber-800 border-amber-300";
    if (pct > 0) return "bg-emerald-100 text-emerald-800 border-emerald-300";
    return "bg-gray-50 text-gray-500";
};

const isoShift = (iso, days) => {
    const d = new Date(`${iso}T00:00:00`);
    d.setDate(d.getDate() + days);
    return d.toISOString().slice(0, 10);
};

export const UtilizationModal = ({ onClose }) => {
    const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        api.get(`/workshop/utilization?from=${from}`)
            .then((r) => setData(r.data))
            .catch(() => setData(null))
            .finally(() => setLoading(false));
    }, [from]);

    const hasUnassigned = (data?.unassigned || []).some((u) => u.booked_min > 0);

    return (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={onClose}>
            <div className="bg-white rounded-sm w-full max-w-4xl max-h-[85vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="utilization-modal">
                <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div className="flex items-center gap-2">
                        <Gauge className="w-5 h-5 text-[#111827]" />
                        <div>
                            <h2 className="font-display font-black text-lg leading-tight">Werkstatt-Auslastung
                                <a href="/dealer/employee-utilization" className="ml-2 text-[11px] font-bold text-sky-700 underline align-middle" data-testid="util-all-sites-link">Alle Standorte →</a>
                            </h2>
                            <div className="text-xs text-gray-600">{data?.location || ""}</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-1.5">
                        <Button size="sm" variant="outline" className="h-8 rounded-sm px-2" onClick={() => setFrom((f) => isoShift(f, -7))} data-testid="util-prev-week"><ChevronLeft className="w-4 h-4" /></Button>
                        <div className="text-sm font-bold min-w-[130px] text-center" data-testid="util-week-label">
                            {data ? `KW ${data.kw} · ${new Date(data.week_from).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}–${new Date(data.week_to).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}` : "…"}
                        </div>
                        <Button size="sm" variant="outline" className="h-8 rounded-sm px-2" onClick={() => setFrom((f) => isoShift(f, 7))} data-testid="util-next-week"><ChevronRight className="w-4 h-4" /></Button>
                        <button onClick={onClose} className="ml-2 p-1.5 rounded-sm hover:bg-gray-100" data-testid="util-close"><X className="w-4 h-4" /></button>
                    </div>
                </div>

                {loading ? (
                    <div className="py-14 text-center text-gray-500"><Loader2 className="w-8 h-8 mx-auto animate-spin" /></div>
                ) : !data ? (
                    <div className="py-10 text-center text-sm text-gray-500">Auslastung konnte nicht geladen werden.</div>
                ) : (
                    <div className="mt-4">
                        <div className="flex items-center gap-3 flex-wrap mb-3">
                            <div className={`rounded-sm border px-3 py-1.5 text-sm font-black ${pctStyle(data.total_pct)}`} data-testid="util-total-pct">
                                Woche gesamt: {data.total_pct != null ? `${data.total_pct} %` : "—"}
                            </div>
                            <div className="text-xs text-gray-600">
                                {fmtH(data.total_booked_min)} verplant von {fmtH(data.total_capacity_min)} Kapazität
                            </div>
                        </div>
                        {data.mechanics.length === 0 ? (
                            <div className="py-8 text-center text-sm text-gray-500 border border-dashed border-gray-300 rounded-sm">
                                Für diesen Standort sind keine Mechaniker hinterlegt (Mitarbeiter mit Funktion &bdquo;Mechaniker&quot;).
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse" data-testid="util-table">
                                    <thead>
                                        <tr className="text-[10px] font-bold uppercase tracking-wider text-gray-500">
                                            <th className="text-left px-2 py-1.5">Mechaniker</th>
                                            {data.days.map((d) => (
                                                <th key={d} className="px-1.5 py-1.5 text-center">
                                                    {new Date(`${d}T00:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}
                                                </th>
                                            ))}
                                            <th className="px-2 py-1.5 text-center">Woche</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.mechanics.map((m) => (
                                            <tr key={m.id} className="border-t border-gray-100" data-testid={`util-row-${m.id}`}>
                                                <td className="px-2 py-2 font-bold whitespace-nowrap">{m.name}</td>
                                                {m.days.map((day) => {
                                                    const pct = day.absent ? null : (day.capacity_min ? Math.round(day.booked_min / day.capacity_min * 100) : null);
                                                    return (
                                                        <td key={day.date} className="px-1 py-1.5 text-center">
                                                            {day.absent ? (
                                                                <span className="inline-block rounded-sm bg-gray-200 text-gray-600 text-[10px] font-bold px-1.5 py-1 w-full">{day.absent}</span>
                                                            ) : day.capacity_min === 0 ? (
                                                                <span className="text-[10px] text-gray-400">geschl.</span>
                                                            ) : (
                                                                <span className={`inline-block rounded-sm border text-[11px] font-bold px-1 py-1 w-full ${pctStyle(pct)}`} title={`${fmtH(day.booked_min)} / ${fmtH(day.capacity_min)}`}>
                                                                    {pct} %
                                                                </span>
                                                            )}
                                                        </td>
                                                    );
                                                })}
                                                <td className="px-2 py-1.5 text-center">
                                                    <span className={`inline-block rounded-sm border text-[11px] font-black px-2 py-1 ${pctStyle(m.pct)}`} data-testid={`util-week-pct-${m.id}`}>
                                                        {m.pct != null ? `${m.pct} %` : "—"}
                                                    </span>
                                                    <div className="text-[9px] text-gray-500 mt-0.5">{fmtH(m.booked_min)} / {fmtH(m.capacity_min)}</div>
                                                </td>
                                            </tr>
                                        ))}
                                        {hasUnassigned && (
                                            <tr className="border-t border-gray-200 bg-amber-50/60" data-testid="util-unassigned-row">
                                                <td className="px-2 py-2 font-bold text-amber-800 whitespace-nowrap">Ohne Mechaniker</td>
                                                {data.unassigned.map((u) => (
                                                    <td key={u.date} className="px-1 py-1.5 text-center text-[11px] font-bold text-amber-800">
                                                        {u.booked_min > 0 ? fmtH(u.booked_min) : "—"}
                                                    </td>
                                                ))}
                                                <td className="px-2 py-1.5 text-center text-[11px] font-black text-amber-800">
                                                    {fmtH(data.unassigned.reduce((s, u) => s + u.booked_min, 0))}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        <div className="mt-3 flex items-center gap-3 text-[10px] text-gray-500">
                            <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-200 border border-emerald-400 mr-1" />frei (&lt;60 %)</span>
                            <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-amber-200 border border-amber-400 mr-1" />gut gefüllt (60–89 %)</span>
                            <span><span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-200 border border-red-400 mr-1" />ausgelastet (≥90 %)</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default UtilizationModal;
